// ExecutionMonitoringPOC.js — Documentation Fonctionnelle : POC « Monitoring d'exécution » (module Performances).
// Aperçu interactif fidèle à la DISPOSITION de l'écran réel « Centre d'Exécution Performance »
// ([PRF][02]-ExecutionMonitoring.js) : en-tête dégradé + indicateur temps réel, 4 cartes KPI,
// liste latérale des exécutions (recherche / filtres de statut / date), en-tête d'exécution
// (retour, statut, durée, boutons Pause/Reprendre/Arrêter, barre de progression), onglets
// Monitoring / Graphiques / Alertes / Logs / Statistiques, rangée de jauges de métriques
// (CPU, Mémoire, Disque, Réseau I/O, Sessions DB, Verrous DB, Processus), mini-graphiques maison
// (sparklines en divs, AUCUNE librairie), indicateurs de tenue des seuils et verdict (PASS/WARNING/FAIL).
// Les actions (Lancer / Pause / Arrêter) sont SIMULÉES localement (montée en charge via setTimeout),
// AUCUN appel réseau. Source de vérité : automata-studio-frontend [PRF][02]-ExecutionMonitoring.js
// + automata_performances (PerformanceExecution · ThresholdEvaluationService.qualify).
import React, { useState } from 'react';
import {
  Play, Square, Pause, RefreshCw, Activity, Cpu, Server, HardDrive, Network, Database, Lock, Hash,
  Gauge, AlertTriangle, CheckCircle, XCircle, Info, Clock, Target, ArrowLeft,
  ArrowRight, Wifi, WifiOff, Award, BarChart3, Layers, BellRing, Terminal, PieChart, PlayCircle,
  PauseCircle, StopCircle, Bell,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// ---- Métriques surveillées (calquées sur METRIC_TYPES de l'écran réel : 7 métriques agent OS/DB) ----
// warn / crit = seuils d'alerte. isGauge => affichage jauge 0–100 %. Sinon valeur + sparkline.
const METRICS = [
  { key: 'CPU_USAGE', label: 'CPU', unit: '%', icon: Cpu, color: '#8b5cf6', base: 12, span: 84, warn: 80, crit: 90, isGauge: true, max: 100 },
  { key: 'MEMORY_USAGE', label: 'Mémoire', unit: '%', icon: Server, color: '#3b82f6', base: 28, span: 62, warn: 80, crit: 90, isGauge: true, max: 100 },
  { key: 'DISK_USAGE', label: 'Disque', unit: '%', icon: HardDrive, color: '#10b981', base: 41, span: 50, warn: 80, crit: 90, isGauge: true, max: 100 },
  { key: 'NETWORK_IO', label: 'Réseau I/O', unit: 'KB/s', icon: Network, color: '#f59e0b', base: 120, span: 1400, warn: 0, crit: 0 },
  { key: 'DB_SESSIONS', label: 'Sessions DB', unit: '', icon: Database, color: '#ec4899', base: 8, span: 70, warn: 0, crit: 0 },
  { key: 'DB_LOCKS', label: 'Verrous DB', unit: '', icon: Lock, color: '#ef4444', base: 0, span: 14, warn: 5, crit: 10 },
  { key: 'PROCESS_COUNT', label: 'Processus', unit: '', icon: Hash, color: '#06b6d4', base: 24, span: 40, warn: 0, crit: 0 },
];

// Verdict d'une métrique selon ses seuils (réplique evaluateSnapshot : OK / WARNING / CRITICAL).
const severityOf = (m, value) => {
  if (!m.warn && !m.crit) return 'ok';
  if (value >= m.crit) return 'crit';
  if (value >= m.warn) return 'warn';
  return 'ok';
};

const SEV = {
  ok: { label: 'OK', dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', ring: 'border-emerald-300 dark:border-emerald-700' },
  warn: { label: 'Avertissement', dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400', ring: 'border-amber-300 dark:border-amber-700' },
  crit: { label: 'Critique', dot: 'bg-red-500', text: 'text-red-600 dark:text-red-400', ring: 'border-red-300 dark:border-red-700' },
};

// Verdict global (réplique ThresholdEvaluationService.qualify : criticals>0 => FAIL, warnings>0 => WARNING, sinon PASS).
const VERDICT = {
  PASS: { label: 'PASS', color: '#10B981', icon: CheckCircle, text: 'text-emerald-600 dark:text-emerald-400', ring: 'border-emerald-500' },
  WARNING: { label: 'WARNING', color: '#F59E0B', icon: AlertTriangle, text: 'text-amber-600 dark:text-amber-400', ring: 'border-amber-500' },
  FAIL: { label: 'FAIL', color: '#EF4444', icon: XCircle, text: 'text-red-600 dark:text-red-400', ring: 'border-red-500' },
  INCONCLUSIVE: { label: 'INCONCLUSIVE', color: '#6B7280', icon: Info, text: 'text-gray-500 dark:text-gray-400', ring: 'border-gray-400' },
};

// Configuration des statuts d'exécution (réplique STATUS_CONFIG).
const STATUS = {
  RUNNING: { label: 'En cours', color: '#3B82F6', icon: Activity },
  PAUSED: { label: 'En pause', color: '#F59E0B', icon: Pause },
  COMPLETED_SUCCESS: { label: 'Succès', color: '#10B981', icon: CheckCircle },
  COMPLETED_FAILURE: { label: 'Échoué', color: '#EF4444', icon: XCircle },
  STOPPED: { label: 'Arrêté', color: '#6B7280', icon: Square },
};

// Liste d'exécutions factices affichée dans la barre latérale (données en dur).
const EXECUTIONS = [
  { id: 'exec-1', name: 'Pic transactionnel — 200 VU', status: 'RUNNING', date: '16/06 09:12', duration: '4m 18s', scenarios: 7, target: 'BGFI-Recette' },
  { id: 'exec-2', name: 'Charge nominale — 50 VU', status: 'COMPLETED_SUCCESS', date: '16/06 08:40', duration: '6m 02s', scenarios: 4, target: 'BGFI-Recette' },
  { id: 'exec-3', name: 'Endurance 30 min — 80 VU', status: 'COMPLETED_FAILURE', date: '15/06 22:05', duration: '30m 00s', scenarios: 5, target: 'AMPLITUDE-Préprod' },
  { id: 'exec-4', name: 'Smoke test — 5 VU', status: 'STOPPED', date: '15/06 18:30', duration: '0m 41s', scenarios: 2, target: 'BGFI-Recette' },
];

const STATUS_FILTERS = [
  { label: 'Tous', value: '' },
  { label: 'En cours', value: 'RUNNING' },
  { label: 'Succès', value: 'COMPLETED_SUCCESS' },
  { label: 'Échoué', value: 'COMPLETED_FAILURE' },
  { label: 'Arrêté', value: 'STOPPED' },
];

const SPARK_LEN = 24;

// Génère une valeur simulée pour la progression p (0→1) avec un peu de bruit.
const sampleValue = (m, p, noise) => {
  const v = m.base + m.span * p + (m.span * 0.1) * noise;
  if (m.isGauge) return Math.max(0, Math.min(m.max, v));
  return Math.max(0, v);
};

const fmt = (m, v) => (m.unit === '%' ? v.toFixed(0) : m.unit === 'KB/s' ? Math.round(v).toLocaleString('fr-FR') : Math.round(v));

// ---- Sous-composants maison (divs uniquement, AUCUNE librairie de graphiques) ----
const Sparkline = ({ points, color }) => {
  const max = Math.max(1, ...points);
  return (
    <div className="flex items-end gap-px h-9">
      {Array.from({ length: SPARK_LEN }).map((_, i) => {
        const v = points[points.length - SPARK_LEN + i];
        const h = v == null ? 0 : Math.max(6, (v / max) * 100);
        return <span key={i} className="flex-1 rounded-sm transition-all duration-300" style={{ height: `${h}%`, backgroundColor: color, opacity: v == null ? 0.15 : 0.5 + (i / SPARK_LEN) * 0.5 }} />;
      })}
    </div>
  );
};

// Jauge circulaire SVG (réplique de CircularGauge, divs+svg natif).
const CircularGauge = ({ value, max, color, warn, crit, size = 84 }) => {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const r = (size - 12) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  const c = pct >= crit ? '#ef4444' : pct >= warn ? '#f59e0b' : color;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="8" fill="none" className="text-gray-200 dark:text-gray-700" stroke="currentColor" />
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="8" fill="none" strokeLinecap="round" stroke={c} strokeDasharray={circ} strokeDashoffset={offset} className="transition-all duration-700 ease-out" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-base font-bold text-gray-900 dark:text-white tabular-nums">{value.toFixed(0)}</span>
        <span className="text-[9px] text-gray-400">%</span>
      </div>
    </div>
  );
};

// Carte KPI de l'en-tête (réplique KpiCard).
const KpiCard = ({ icon: IconCmp, label, value, color }) => (
  <div className="p-4 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20">
    <div className="flex items-center justify-between mb-2">
      <span className="p-2 rounded-xl bg-white/20"><IconCmp className="h-5 w-5 text-white" /></span>
    </div>
    <p className="text-2xl font-bold text-white tabular-nums">{value}</p>
    <p className="text-xs text-white/70 mt-0.5">{label}</p>
  </div>
);

// Carte de métrique du tableau de bord temps réel (jauge ou valeur + sparkline + tenue de seuil).
const MetricTile = ({ m, value, history, running }) => {
  const sev = severityOf(m, value);
  const cfg = SEV[sev];
  const IconCmp = m.icon;
  const hasThreshold = (m.warn || 0) > 0;
  return (
    <div className={`p-4 rounded-2xl border bg-white dark:bg-gray-800/60 ${sev === 'ok' ? 'border-gray-200 dark:border-gray-700' : cfg.ring}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="p-2 rounded-xl" style={{ backgroundColor: m.color + '22' }}><IconCmp className="h-4 w-4" style={{ color: m.color }} /></span>
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{m.label}</span>
        </div>
        {hasThreshold && <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${cfg.text}`}><span className={`h-2 w-2 rounded-full ${cfg.dot}`} />{cfg.label}</span>}
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{fmt(m, value)}</span>
          {m.unit && <span className="text-xs text-gray-400">{m.unit}</span>}
        </div>
        {m.isGauge && <CircularGauge value={value} max={m.max} color={m.color} warn={m.warn} crit={m.crit} />}
      </div>
      {!m.isGauge && <div className="mt-3"><Sparkline points={history} color={m.color} /></div>}
      {hasThreshold && <p className="mt-2 text-[10px] text-gray-400">Seuil alerte {m.warn}{m.unit} · critique {m.crit}{m.unit}</p>}
      {!running && history.length <= 1 && <p className="mt-1 text-[10px] text-gray-400">En attente de lancement…</p>}
    </div>
  );
};

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page ----
const ExecutionMonitoringPOC = () => {
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [finished, setFinished] = useState(false);
  const [progress, setProgress] = useState(0); // 0 → 1, montée en charge simulée
  const [values, setValues] = useState(() => Object.fromEntries(METRICS.map((m) => [m.key, m.base])));
  const [history, setHistory] = useState(() => Object.fromEntries(METRICS.map((m) => [m.key, [m.base]])));
  const [selectedExec, setSelectedExec] = useState('exec-1');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tab, setTab] = useState('monitoring');
  const [tick, setTick] = useState(0);

  const exec = EXECUTIONS.find((e) => e.id === selectedExec) || EXECUTIONS[0];
  const filteredExecs = EXECUTIONS.filter((e) =>
    (!statusFilter || e.status === statusFilter)
    && `${e.name} ${e.target}`.toLowerCase().includes(search.toLowerCase()));

  const hasStarted = running || paused || finished;
  const liveSeverities = METRICS.map((m) => severityOf(m, values[m.key]));
  const verdictKey = !hasStarted ? 'INCONCLUSIVE'
    : liveSeverities.includes('crit') ? 'FAIL'
    : liveSeverities.includes('warn') ? 'WARNING' : 'PASS';
  const verdict = VERDICT[verdictKey];
  const VerdictIcon = verdict.icon;

  // Score = part des métriques à seuil dans le vert (réplique calculateScore : passed / totalChecked).
  const checked = METRICS.filter((m) => (m.warn || 0) > 0);
  const passed = checked.filter((m) => severityOf(m, values[m.key]) === 'ok').length;
  const score = !hasStarted ? 0 : Math.round((passed / checked.length) * 100);

  const violations = METRICS.filter((m) => severityOf(m, values[m.key]) !== 'ok')
    .map((m) => ({ m, sev: severityOf(m, values[m.key]), value: values[m.key] }));

  // KPI de l'en-tête (réplique kpis : total / en cours / succès / échouées) sur les données en dur.
  const kpis = {
    total: EXECUTIONS.length,
    running: EXECUTIONS.filter((e) => e.status === 'RUNNING').length,
    success: EXECUTIONS.filter((e) => e.status === 'COMPLETED_SUCCESS').length,
    failed: EXECUTIONS.filter((e) => e.status === 'COMPLETED_FAILURE').length,
  };

  // ----- Boucle de montée en charge simulée (setTimeout, pas de réseau) -----
  React.useEffect(() => {
    if (!running) return undefined;
    const id = window.setTimeout(() => {
      setProgress((prev) => {
        const next = Math.min(1, prev + 0.045);
        const noise = Math.sin(tick * 1.7) * 0.5 + (Math.random() - 0.5);
        const newValues = {};
        METRICS.forEach((m) => { newValues[m.key] = sampleValue(m, next, noise); });
        setValues(newValues);
        setHistory((h) => {
          const out = {};
          METRICS.forEach((m) => {
            const arr = [...(h[m.key] || []), newValues[m.key]];
            out[m.key] = arr.length > 60 ? arr.slice(-60) : arr;
          });
          return out;
        });
        if (next >= 1) { setRunning(false); setFinished(true); }
        return next;
      });
      setTick((t) => t + 1);
    }, 600);
    return () => window.clearTimeout(id);
  }, [running, tick]);

  const start = () => {
    setFinished(false); setPaused(false); setProgress(0);
    setValues(Object.fromEntries(METRICS.map((m) => [m.key, m.base])));
    setHistory(Object.fromEntries(METRICS.map((m) => [m.key, [m.base]])));
    setRunning(true); setTab('monitoring');
  };
  const togglePause = () => { if (running) { setRunning(false); setPaused(true); } else if (paused) { setPaused(false); setRunning(true); } };
  const stop = () => { setRunning(false); setPaused(false); setFinished(true); };

  const isActive = running || paused;
  const headerStatus = running ? STATUS.RUNNING : paused ? STATUS.PAUSED
    : finished ? (verdictKey === 'FAIL' ? STATUS.COMPLETED_FAILURE : STATUS.COMPLETED_SUCCESS)
    : STATUS[exec.status];
  const HStatusIcon = headerStatus.icon;

  return (
    <div className="space-y-8">
      {/* ===== Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Monitoring d'exécution</h1>
          <Badge color="#EF4444">Module Performances</Badge>
          <Badge color="#3B82F6">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Aperçu interactif de l'écran « Centre d'Exécution Performance » (démo, actions simulées localement).
        </p>
      </div>

      {/* ===== Aperçu interactif de l'IHM ===== */}
      <Card className="overflow-hidden">
        {/* En-tête dégradé : titre + indicateur temps réel + actions globales + 4 KPI */}
        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-700 px-5 py-5 text-white">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div className="flex items-center gap-3">
              <span className="p-2.5 bg-white/10 rounded-xl backdrop-blur-sm"><Activity className="h-6 w-6 text-white" /></span>
              <div>
                <h2 className="text-xl font-bold leading-tight">Centre d'Exécution Performance</h2>
                <p className="text-violet-200 text-sm mt-0.5">Monitoring temps réel des exécutions de test de performance</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${running ? 'bg-emerald-400/30 text-white' : 'bg-white/10 text-white/80'}`}>
                {running ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}{running ? 'Connecté' : 'Déconnecté'}
              </span>
              <button onClick={start} disabled={running} className="p-2.5 bg-white/10 hover:bg-white/20 rounded-xl transition-colors disabled:opacity-40" title="Rafraîchir / relancer">
                <RefreshCw className={`h-5 w-5 ${running ? 'animate-spin' : ''}`} />
              </button>
              <Button onClick={start} className="bg-white text-violet-700 hover:bg-violet-50"><PlayCircle className="h-5 w-5" />Nouvelle exécution</Button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard icon={Layers} label="Total exécutions" value={kpis.total} />
            <KpiCard icon={Activity} label="En cours" value={kpis.running + (running ? 1 : 0)} />
            <KpiCard icon={CheckCircle} label="Succès" value={kpis.success} />
            <KpiCard icon={XCircle} label="Échouées" value={kpis.failed} />
          </div>
        </div>

        <CardContent className="space-y-5">
          <div className="flex flex-col lg:flex-row gap-5">
            {/* ----- Barre latérale : liste des exécutions ----- */}
            <div className="lg:w-72 lg:shrink-0">
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-3 py-2.5 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Exécutions</h3>
                </div>
                <div className="p-3 space-y-2 border-b border-gray-200 dark:border-gray-700">
                  <SearchBar value={search} onChange={setSearch} placeholder="Rechercher…" />
                  <div className="flex gap-1 flex-wrap">
                    {STATUS_FILTERS.map((sf) => (
                      <button key={sf.value} onClick={() => setStatusFilter(sf.value)}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-colors ${statusFilter === sf.value ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                        {sf.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/60">
                  {filteredExecs.length === 0
                    ? <EmptyState icon={Activity} title="Aucune exécution" message="Aucune exécution ne correspond." className="py-8" />
                    : filteredExecs.map((e) => {
                      const st = STATUS[e.status];
                      const StIcon = st.icon;
                      return (
                        <button key={e.id} onClick={() => setSelectedExec(e.id)}
                          className={`w-full text-left p-3 transition-colors border-l-4 ${selectedExec === e.id ? 'bg-violet-50 dark:bg-violet-900/20 border-l-violet-500' : 'border-l-transparent hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{e.name}</p>
                              <p className="text-[10px] text-gray-400 mt-0.5">{e.date} · {e.duration}</p>
                            </div>
                            <Badge color={st.color}><StIcon className="h-3 w-3" />{st.label}</Badge>
                          </div>
                        </button>
                      );
                    })}
                </div>
              </div>
            </div>

            {/* ----- Panneau principal ----- */}
            <div className="flex-1 min-w-0 space-y-5">
              {/* En-tête d'exécution : retour, statut, durée, boutons d'action, progression */}
              <div className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="p-2 rounded-xl bg-gray-100 dark:bg-gray-700"><ArrowLeft className="h-5 w-5 text-gray-500" /></span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white truncate">{exec.name}</h2>
                        <Badge color={headerStatus.color}><HStatusIcon className="h-3 w-3" />{headerStatus.label}</Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                        <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{exec.date}</span>
                        <span className="flex items-center gap-1"><Target className="h-3.5 w-3.5" />Durée : {exec.duration}</span>
                        <span className="flex items-center gap-1"><Hash className="h-3.5 w-3.5" />{exec.scenarios} scénarios · {exec.target}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isActive ? (
                      <>
                        <button onClick={togglePause}
                          className="flex items-center gap-1.5 px-4 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-xl text-sm font-medium hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors">
                          {paused ? <PlayCircle className="h-4 w-4" /> : <PauseCircle className="h-4 w-4" />}{paused ? 'Reprendre' : 'Pause'}
                        </button>
                        <button onClick={stop}
                          className="flex items-center gap-1.5 px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-xl text-sm font-medium hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors">
                          <StopCircle className="h-4 w-4" />Arrêter
                        </button>
                      </>
                    ) : (
                      <Button onClick={start} className="bg-gradient-to-r from-violet-600 to-purple-700 text-white hover:from-violet-700 hover:to-purple-800"><Play className="h-4 w-4" />Lancer</Button>
                    )}
                  </div>
                </div>

                {/* Barre de progression (montée en charge) */}
                {(isActive || finished) && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400 font-medium">{running ? 'Montée en charge en cours…' : paused ? 'En pause' : 'Exécution terminée'}</span>
                      <span className="font-bold text-gray-900 dark:text-white tabular-nums">{Math.round(progress * 100)}%</span>
                    </div>
                    <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-purple-600 transition-all duration-500" style={{ width: `${progress * 100}%` }} />
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                      <span className="flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5 text-emerald-500" />{Math.round(progress * exec.scenarios)} terminées</span>
                      <span className="flex items-center gap-1"><XCircle className="h-3.5 w-3.5 text-red-500" />{verdictKey === 'FAIL' ? 1 : 0} échouées</span>
                      <span className="flex items-center gap-1"><Layers className="h-3.5 w-3.5 text-gray-400" />{exec.scenarios} total</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Onglets (réplique des 5 onglets de l'écran réel) */}
              <Tabs
                tabs={[
                  { id: 'monitoring', label: 'Monitoring' },
                  { id: 'charts', label: 'Graphiques' },
                  { id: 'alerts', label: 'Alertes', count: violations.length || undefined },
                  { id: 'logs', label: 'Logs' },
                  { id: 'statistics', label: 'Statistiques' },
                ]}
                active={tab}
                onChange={setTab}
              />

              {/* ----- Onglet Monitoring : jauges temps réel + verdict ----- */}
              {tab === 'monitoring' && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                      <Gauge className="h-4 w-4 text-violet-500" />Métriques en temps réel
                      {running && <span className="h-2 w-2 rounded-full bg-violet-500 animate-pulse" />}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                      {METRICS.map((m) => (
                        <MetricTile key={m.key} m={m} value={values[m.key]} history={history[m.key]} running={running} />
                      ))}
                      {/* Carte verdict global */}
                      <div className={`p-4 rounded-2xl border-2 ${verdict.ring} bg-white dark:bg-gray-800/60 flex flex-col items-center justify-center text-center`}>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Verdict global</p>
                        <VerdictIcon className={`h-8 w-8 mb-1 ${verdict.text}`} />
                        <Badge color={verdict.color}>{verdict.label}</Badge>
                        <div className="mt-3 w-full">
                          <div className="flex items-center justify-between text-[10px] text-gray-400 mb-1"><span>Score</span><span className="tabular-nums">{score}%</span></div>
                          <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${score}%`, backgroundColor: verdict.color }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ----- Onglet Graphiques : sparklines détaillées par métrique ----- */}
              {tab === 'charts' && (
                !hasStarted ? (
                  <EmptyState icon={BarChart3} title="Aucune donnée disponible" message="Les graphiques apparaîtront lorsque des métriques seront collectées." className="py-10" />
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {METRICS.filter((m) => !m.isGauge || true).map((m) => {
                      const data = history[m.key] || [];
                      const min = Math.min(...data), max = Math.max(...data), avg = data.reduce((s, v) => s + v, 0) / data.length;
                      return (
                        <div key={m.key} className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2"><m.icon className="h-4 w-4" style={{ color: m.color }} />{m.label}</h4>
                            <span className="text-sm font-bold" style={{ color: m.color }}>{fmt(m, values[m.key])} {m.unit}</span>
                          </div>
                          <Sparkline points={data} color={m.color} />
                          <div className="flex items-center gap-3 text-[10px] text-gray-500 dark:text-gray-400 mt-2">
                            <span>Min {fmt(m, min)}{m.unit}</span><span>Max {fmt(m, max)}{m.unit}</span><span>Moy {fmt(m, avg)}{m.unit}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {/* ----- Onglet Alertes : violations de seuils ----- */}
              {tab === 'alerts' && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <BellRing className="h-4 w-4 text-red-500" />Violations de seuils
                    <span className="px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-bold">{violations.length}</span>
                  </h3>
                  {violations.length === 0 ? (
                    <div className="text-center py-10">
                      <Bell className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">Aucune violation de seuil détectée</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {violations.map(({ m, sev, value }) => {
                        const cfg = SEV[sev];
                        const SevIcon = sev === 'crit' ? XCircle : AlertTriangle;
                        return (
                          <div key={m.key} className={`p-3 rounded-xl border flex items-start gap-3 ${cfg.ring} ${sev === 'crit' ? 'bg-red-50 dark:bg-red-900/20' : 'bg-amber-50 dark:bg-amber-900/20'}`}>
                            <SevIcon className={`h-5 w-5 mt-0.5 shrink-0 ${cfg.text}`} />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-semibold ${cfg.text}`}>{m.label} — {cfg.label}</p>
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">Valeur : <strong>{fmt(m, value)}{m.unit}</strong> · Seuil : <strong>{(sev === 'crit' ? m.crit : m.warn)}{m.unit}</strong></p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ----- Onglet Logs : console simulée ----- */}
              {tab === 'logs' && (
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2"><Terminal className="h-4 w-4 text-violet-500" />Logs d'exécution</h3>
                  </div>
                  <div className="bg-gray-950 max-h-64 overflow-y-auto p-3 font-mono text-xs space-y-1">
                    {!hasStarted ? (
                      <p className="text-gray-500 text-center py-6">En attente de logs…</p>
                    ) : (
                      <>
                        <p><span className="text-gray-500">09:12:01</span> <span className="text-cyan-400">[INFO]</span> <span className="text-gray-300">Démarrage de l'exécution « {exec.name} »</span></p>
                        <p><span className="text-gray-500">09:12:02</span> <span className="text-cyan-400">[INFO]</span> <span className="text-gray-300">Montée en charge {Math.round(progress * 100)}% · {Math.round(values.DB_SESSIONS)} sessions DB</span></p>
                        {violations.map(({ m, sev, value }) => (
                          <p key={m.key}><span className="text-gray-500">09:12:0{sev === 'crit' ? '5' : '3'}</span> <span className={sev === 'crit' ? 'text-red-400' : 'text-amber-400'}>[{sev === 'crit' ? 'ERROR' : 'WARN'}]</span> <span className="text-gray-300">Seuil {m.label} : {fmt(m, value)}{m.unit}</span></p>
                        ))}
                        {finished && <p><span className="text-gray-500">09:16:19</span> <span className={verdictKey === 'FAIL' ? 'text-red-400' : 'text-emerald-400'}>[{verdictKey === 'FAIL' ? 'ERROR' : 'SUCCESS'}]</span> <span className="text-gray-300">Terminé · Qualification : {verdict.label} · Score : {score}%</span></p>}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* ----- Onglet Statistiques : qualification + score + distribution + résumé ----- */}
              {tab === 'statistics' && (
                !hasStarted ? (
                  <EmptyState icon={PieChart} title="Aucune statistique" message="Lancez une exécution pour afficher les statistiques complètes." className="py-10" />
                ) : (
                  <div className="space-y-4">
                    {/* Qualification + score */}
                    <div className="p-6 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 flex items-center justify-center gap-10 flex-wrap">
                      <div className="text-center">
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Qualification</p>
                        <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-lg border-2 ${verdict.ring} ${verdict.text}`}><VerdictIcon className="h-6 w-6" />{verdict.label}</div>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Score global</p>
                        <div className="relative w-24 h-24 mx-auto">
                          <svg width={96} height={96} className="-rotate-90">
                            <circle cx={48} cy={48} r={40} strokeWidth="9" fill="none" className="text-gray-200 dark:text-gray-700" stroke="currentColor" />
                            <circle cx={48} cy={48} r={40} strokeWidth="9" fill="none" strokeLinecap="round" stroke={verdict.color} strokeDasharray={2 * Math.PI * 40} strokeDashoffset={2 * Math.PI * 40 * (1 - score / 100)} className="transition-all duration-700" />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{score}</span><span className="text-[10px] text-gray-400">%</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Distribution des tâches (barres maison) */}
                    <div className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2"><PieChart className="h-4 w-4 text-violet-500" />Distribution des tâches</h4>
                      {(() => {
                        const ok = verdictKey === 'FAIL' ? exec.scenarios - 1 : exec.scenarios;
                        const ko = verdictKey === 'FAIL' ? 1 : 0;
                        const dist = [{ name: 'Réussies', value: ok, color: '#10b981' }, { name: 'Échouées', value: ko, color: '#ef4444' }].filter((d) => d.value > 0);
                        const tot = dist.reduce((s, d) => s + d.value, 0) || 1;
                        return (
                          <div className="space-y-2">
                            {dist.map((d) => (
                              <div key={d.name} className="flex items-center gap-3">
                                <span className="w-20 text-xs text-gray-600 dark:text-gray-400">{d.name}</span>
                                <div className="flex-1 h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(d.value / tot) * 100}%`, backgroundColor: d.color }} />
                                </div>
                                <span className="w-8 text-xs font-semibold text-gray-700 dark:text-gray-300 text-right tabular-nums">{d.value}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Résumé des métriques (réplique tableau avg/min/max) */}
                    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700"><h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-violet-500" />Résumé des métriques</h4></div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                            <tr>{['Métrique', 'Moyenne', 'Min', 'Max'].map((h, i) => <th key={h} className={`py-2 px-3 font-medium ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>)}</tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                            {METRICS.map((m) => {
                              const data = history[m.key] || [];
                              const min = Math.min(...data), max = Math.max(...data), avg = data.reduce((s, v) => s + v, 0) / data.length;
                              return (
                                <tr key={m.key} className="text-gray-700 dark:text-gray-300">
                                  <td className="py-2.5 px-3"><span className="flex items-center gap-2 font-medium"><m.icon className="h-4 w-4" style={{ color: m.color }} />{m.label}</span></td>
                                  <td className="py-2.5 px-3 text-right font-mono tabular-nums">{fmt(m, avg)} {m.unit}</td>
                                  <td className="py-2.5 px-3 text-right font-mono tabular-nums">{fmt(m, min)} {m.unit}</td>
                                  <td className="py-2.5 px-3 text-right font-mono tabular-nums">{fmt(m, max)} {m.unit}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ===== Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Play className="h-5 w-5 text-violet-500" />1. Lancer une exécution</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Choisir une <strong>exécution</strong> dans la liste latérale (recherche + filtres de statut). <button onClick={() => setSelectedExec('exec-1')} className="text-blue-600 dark:text-blue-400 underline">Sélectionner</button></GuideStep>
              <GuideStep n={2}>Cliquer sur <strong>« Lancer »</strong> ou <strong>« Nouvelle exécution »</strong>. <button onClick={start} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={3}>Suivre la <strong>montée en charge</strong> via la barre de progression (Pause / Arrêter disponibles).</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Gauge className="h-5 w-5 text-violet-500" />2. Suivre les métriques</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Onglet <strong>Monitoring</strong> : jauges temps réel (CPU, Mémoire, Disque) + valeurs (Réseau, DB, Processus). <button onClick={() => setTab('monitoring')} className="text-blue-600 dark:text-blue-400 underline">Ouvrir</button></GuideStep>
              <GuideStep n={2}>Onglet <strong>Graphiques</strong> : évolution détaillée (min / max / moyenne) par métrique. <button onClick={() => setTab('charts')} className="text-blue-600 dark:text-blue-400 underline">Ouvrir</button></GuideStep>
              <GuideStep n={3}>Onglet <strong>Alertes</strong> : violations de seuils (avertissement / critique) au fil de l'eau.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Award className="h-5 w-5 text-violet-500" />3. Lire le verdict</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Carte <strong>Verdict global</strong> (onglet Monitoring) : qualification + score en direct.</GuideStep>
              <GuideStep n={2}>Onglet <strong>Statistiques</strong> : qualification, score, distribution des tâches, résumé des métriques. <button onClick={() => setTab('statistics')} className="text-blue-600 dark:text-blue-400 underline">Ouvrir</button></GuideStep>
              <GuideStep n={3}>Verdict <span className="text-emerald-600">PASS</span> / <span className="text-amber-600">WARNING</span> / <span className="text-red-600">FAIL</span> selon les seuils franchis : à forte charge, CPU et verrous DB basculent le verdict.</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== Référence : métriques surveillées ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><BarChart3 className="h-5 w-5 text-violet-500" />Métriques surveillées</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>{['Métrique', 'Unité', 'Seuil alerte', 'Seuil critique', 'Affichage'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {METRICS.map((m) => (
                <tr key={m.key} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2 font-medium"><span className="flex items-center gap-2"><m.icon className="h-4 w-4" style={{ color: m.color }} />{m.label}</span></td>
                  <td className="px-4 py-2">{m.unit || '—'}</td>
                  <td className="px-4 py-2">{(m.warn || 0) > 0 ? `${m.warn}${m.unit}` : '—'}</td>
                  <td className="px-4 py-2">{(m.crit || 0) > 0 ? `${m.crit}${m.unit}` : '—'}</td>
                  <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{m.isGauge ? 'Jauge 0–100 %' : 'Valeur + sparkline'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* ===== Règles de gestion (verdict) + Endpoints API ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Règles de gestion — verdict selon seuils</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Au moins un seuil <strong>critique</strong> dépassé → <Badge color="#EF4444">FAIL</Badge></li>
            <li className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />Au moins un seuil <strong>d'alerte</strong> dépassé, aucun critique → <Badge color="#F59E0B">WARNING</Badge></li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />Tous les seuils respectés → <Badge color="#10B981">PASS</Badge></li>
            <li className="flex items-start gap-2"><Info className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />Aucune mesure (ou aucun seuil défini) → <Badge>INCONCLUSIVE</Badge></li>
            <li className="flex items-start gap-2"><Target className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" /><strong>Score</strong> = part des mesures sous les seuils (mesures conformes ÷ mesures contrôlées).</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints API</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['POST', '#3B82F6', '/api/automata/performance/executions/test-plans/{id}/start'],
              ['POST', '#F59E0B', '/api/automata/performance/executions/{id}/pause'],
              ['POST', '#EF4444', '/api/automata/performance/executions/{id}/stop'],
              ['GET', '#10B981', '/api/automata/performance/executions/running'],
              ['GET', '#10B981', '/api/automata/performance/executions/{id}/statistics'],
              ['GET', '#10B981', '/api/automata/performance/metrics/execution/{id}/latest'],
              ['GET', '#10B981', '/api/automata/performance/metrics/execution/{id}/threshold-evaluation'],
              ['WS', '#8B5CF6', '/ws/performance (METRIC_UPDATE · THRESHOLD_VIOLATION · EXECUTION_COMPLETE)'],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_performances / PerformanceExecutionService · ThresholdEvaluationService</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default ExecutionMonitoringPOC;
